/*
    Split Quill HTML into words that remember their own formatting, so line-balancing code can
    work on plain words and the formatting can be put back afterwards.

    tokenize(html)            -> { paragraphs: [["word", ...], ...], words: [{ text, segments }] } or null
    toPlainHtml(tokens)       -> "<p>word word</p><p>word</p>" (no inline tags, entities kept)
    rebuildLines(lines, tokens) -> one HTML string per balanced line, with tags re-opened at the start
                                   and closed at the end of every line, or null when the balanced
                                   lines don't contain exactly the original words in order.

    String based (no DOM) so it also runs in Node during imports.
*/

const VOID_TAGS = new Set(["br", "img", "hr", "wbr"]);
function tagName(tag) {
  const match = tag.match(/^<\/?\s*([a-zA-Z][\w-]*)/);
  return match ? match[1].toLowerCase() : "";
}
function stackKey(stack) {
  return stack.map(entry => entry.raw).join("");
}
function tokenize(html) {
  if (!html || /<\s*(ruby|rt)\b/i.test(html)) {
    return null;
  }
  const words = [];
  const paragraphs = [];
  let paragraph = null;
  let stack = [];
  let word = null;
  const endWord = () => {
    word = null;
  };
  const ensureParagraph = () => {
    if (!paragraph) {
      paragraph = [];
      paragraphs.push(paragraph);
    }
  };
  const addChar = ch => {
    ensureParagraph();
    if (!word) {
      word = {
        text: "",
        segments: []
      };
      words.push(word);
      paragraph.push(word);
    }
    const key = stackKey(stack);
    let segment = word.segments[word.segments.length - 1];
    if (!segment || segment.key !== key) {
      segment = {
        key,
        stack: stack.slice(),
        text: ""
      };
      word.segments.push(segment);
    }
    segment.text += ch;
    word.text += ch;
  };
  const parts = html.match(/<[^>]*>|[^<]+/g) || [];
  for (const part of parts) {
    if (part.startsWith("<")) {
      const name = tagName(part);
      const closing = /^<\s*\//.test(part);
      if (name === "p") {
        endWord();
        if (closing) {
          paragraph = null;
        } else {
          paragraph = [];
          paragraphs.push(paragraph);
        }
        stack = [];
      } else if (name === "br") {
        endWord();
      } else if (closing) {
        const at = stack.map(entry => entry.name).lastIndexOf(name);
        if (at > -1) {
          stack = stack.slice(0, at);
        }
      } else if (name && !VOID_TAGS.has(name) && !/\/\s*>$/.test(part)) {
        stack = [...stack, {
          name,
          raw: part
        }];
      }
      continue;
    }
    for (const ch of part) {
      if (/\s/.test(ch)) {
        endWord();
      } else {
        addChar(ch);
      }
    }
  }
  if (words.length === 0) {
    return null;
  }
  return {
    words,
    paragraphs: paragraphs.filter(p => p.length > 0)
  };
}
function toPlainHtml(tokens) {
  return tokens.paragraphs.map(paragraph => `<p>${paragraph.map(w => w.text).join(" ")}</p>`).join("");
}
function commonPrefixLength(a, b) {
  let i = 0;
  while (i < a.length && i < b.length && a[i].raw === b[i].raw) {
    i++;
  }
  return i;
}
function serializeLine(lineWords) {
  let out = "";
  let current = [];
  const moveTo = target => {
    const keep = commonPrefixLength(current, target);
    for (let i = current.length - 1; i >= keep; i--) {
      out += `</${current[i].name}>`;
    }
    for (let i = keep; i < target.length; i++) {
      out += target[i].raw;
    }
    current = target;
  };
  lineWords.forEach((word, index) => {
    if (index > 0) {
      //The space between two words keeps only the formatting both sides share
      const next = word.segments[0].stack;
      moveTo(current.slice(0, commonPrefixLength(current, next)));
      out += " ";
    }
    word.segments.forEach(segment => {
      moveTo(segment.stack);
      out += segment.text;
    });
  });
  moveTo([]);
  return out;
}
function rebuildLines(lines, tokens) {
  if (!Array.isArray(lines) || !tokens) {
    return null;
  }
  let index = 0;
  const result = [];
  for (const line of lines) {
    if (typeof line !== "string" || line.includes("\n") || /<[^>]*>/.test(line.replace(/<\/?p\b[^>]*>/gi, ""))) {
      return null;
    }
    const lineWords = line.replace(/<\/?p\b[^>]*>/gi, " ").split(/\s+/).filter(Boolean);
    const tokenWords = [];
    for (const text of lineWords) {
      const token = tokens.words[index];
      if (!token || token.text !== text) {
        return null;
      }
      tokenWords.push(token);
      index++;
    }
    result.push(serializeLine(tokenWords));
  }
  return index === tokens.words.length ? result : null;
}
export default {
  tokenize,
  toPlainHtml,
  rebuildLines
};